/**
 * firestoreSdk — the single lazy-load boundary for `firebase/firestore`.
 *
 * `firebase/firestore` is ~155KB gzip, the single largest dependency in the
 * app. This app is offline-first (persistentLocalCache is load-bearing), so
 * the SDK is still required early — but it does not need to be in the
 * initial modulepreloaded payload. Every Firestore-touching hook
 * (useFirestoreDoc, useFirestoreCollection, useFirestoreMutation, and the
 * query-constraint builders in useUserVehicles/useTransferStatus/etc.) MUST
 * go through `getFirestoreSdk()` rather than a static
 * `import ... from 'firebase/firestore'` — a static import anywhere in the
 * graph reachable from the entry defeats the dynamic-import chunk boundary
 * (see the rolldown INEFFECTIVE_DYNAMIC_IMPORT warning this codebase already
 * hits for src/lib/serviceQueue.ts).
 *
 * `getFirestoreSdk()` is memoized: the SDK is fetched and `initializeFirestore`
 * runs exactly once per app session, on first call. The persistentLocalCache
 * is guaranteed to be established before the caller's first `doc`/`collection`/
 * `onSnapshot`/`setDoc` call, preserving the offline-first init-before-use
 * ordering this app depends on — just deferred from "app boot" to "first
 * Firestore-backed hook actually runs" (in practice, moments later: right
 * after auth resolves and VehicleList mounts).
 *
 * Capacitor migration boundary: this module (plus useFirestoreMutation.ts)
 * is where a Capacitor wrap would swap in `@capacitor-firebase/firestore`.
 */

import type * as FirestoreModule from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';
import { env } from './env';
import { app } from './firebase';

export type FirestoreSdk = typeof FirestoreModule;

let sdkPromise: Promise<FirestoreSdk> | null = null;
let dbPromise: Promise<Firestore> | null = null;

function loadSdk(): Promise<FirestoreSdk> {
  sdkPromise ??= import('firebase/firestore');
  return sdkPromise;
}

async function initDb(sdk: FirestoreSdk): Promise<Firestore> {
  // In emulator mode, persistent IndexedDB cache plays poorly with the
  // per-spec reset cycle, so skip it for cleaner test isolation.
  const db = env.VITE_USE_FIREBASE_EMULATOR
    ? sdk.initializeFirestore(app, {})
    : sdk.initializeFirestore(app, {
        localCache: sdk.persistentLocalCache({
          tabManager: sdk.persistentMultipleTabManager(),
        }),
      });

  if (env.VITE_USE_FIREBASE_EMULATOR) {
    sdk.connectFirestoreEmulator(db, '127.0.0.1', 8080);
  }

  return db;
}

/**
 * Resolves the Firestore SDK module namespace plus the initialized Firestore
 * instance. Safe to call from multiple hooks concurrently — the underlying
 * dynamic import and `initializeFirestore` call each run exactly once.
 */
export async function getFirestoreSdk(): Promise<{
  sdk: FirestoreSdk;
  db: Firestore;
}> {
  const sdk = await loadSdk();
  dbPromise ??= initDb(sdk);
  const db = await dbPromise;
  return { sdk, db };
}
